import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ResearchPage = () => {
  const [activeTab, setActiveTab] = useState('publications');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const publications = [
    {
      id: 1,
      title: "Electromagnetic Field Computation: A New Paradigm for Distributed Systems",
      authors: "Dr. Amara Okafor, Prof. Chen Wei, Dr. Sarah Johnson",
      journal: "Nature Computational Science",
      year: 2024,
      citations: 127,
      category: "foundational",
      abstract: "We present a revolutionary approach to distributed computing that leverages electromagnetic field dynamics...",
      tags: ["EADC", "Field Theory", "Distributed Systems"]
    },
    {
      id: 2,
      title: "Breaking the CAP Theorem: Physical Laws as Computational Guarantees",
      authors: "Prof. Kwame Asante, Dr. Elena Volkov, Dr. James Mitchell",
      journal: "Science",
      year: 2024,
      citations: 89,
      category: "theoretical",
      abstract: "The CAP theorem has long been considered a fundamental limitation in distributed systems. We demonstrate...",
      tags: ["CAP Theorem", "Physics", "Theory"]
    },
    {
      id: 3,
      title: "LightFold: Continuous Electromagnetic Computing at Scale",
      authors: "Dr. Fatima Al-Rashid, Prof. David Kim, Dr. Maria Santos",
      journal: "IEEE Transactions on Quantum Engineering",
      year: 2023,
      citations: 156,
      category: "implementation",
      abstract: "LightFold represents the first practical implementation of electromagnetic field computation...",
      tags: ["LightFold", "Implementation", "Scalability"]
    },
    {
      id: 4,
      title: "Noise as Fuel: Harvesting Environmental Entropy for Computation",
      authors: "Dr. Yuki Tanaka, Prof. Robert Brown, Dr. Aisha Patel",
      journal: "Physical Review Letters",
      year: 2023,
      citations: 203,
      category: "foundational",
      abstract: "We demonstrate how environmental noise can be transformed into computational fuel through coherent interference...",
      tags: ["Noise", "Energy", "Quantum"]
    }
  ];

  const patents = [
    {
      id: "US11234567B2",
      title: "Electromagnetic Field Modulation for Distributed Computation",
      inventors: "Okafor, A., Wei, C., Johnson, S.",
      filed: "2022-03-15",
      granted: "2023-11-20",
      status: "active"
    },
    {
      id: "EP3987654A1",
      title: "Continuous Field Processing Architecture",
      inventors: "Asante, K., Volkov, E.",
      filed: "2022-06-10",
      granted: "2024-01-15",
      status: "active"
    },
    {
      id: "WO2023/123456",
      title: "Quantum Phase Readout for Electromagnetic Computing",
      inventors: "Al-Rashid, F., Kim, D.",
      filed: "2023-02-20",
      granted: "Pending",
      status: "pending"
    }
  ];

  const collaborations = [
    {
      institution: "MIT Quantum Engineering Center",
      focus: "Quantum field theory applications in computing",
      lead: "Prof. Michael Chen",
      status: "Active",
      startDate: "2022"
    },
    {
      institution: "University of Nairobi - Advanced Computing Lab",
      focus: "African mathematical foundations in modern computing",
      lead: "Dr. Wanjiru Kamau",
      status: "Active",
      startDate: "2021"
    },
    {
      institution: "CERN - Computational Physics Division",
      focus: "High-energy physics simulations using EADC",
      lead: "Dr. François Dubois",
      status: "Active",
      startDate: "2023"
    },
    {
      institution: "Stanford AI Lab",
      focus: "Continuous intelligence architectures",
      lead: "Prof. Lisa Wang",
      status: "Active",
      startDate: "2023"
    }
  ];

  const filteredPublications = publications.filter(pub =>
    pub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pub.authors.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pub.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <style>{`
        .research-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--blue-50), var(--gray-50));
          position: relative;
          overflow: hidden;
        }

        .research-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, var(--blue-100) 0%, transparent 70%);
          opacity: 0.3;
          animation: rotate 30s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .research-hero-content {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .research-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--white);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--blue-600);
          margin-bottom: 2rem;
          box-shadow: var(--shadow-sm);
        }

        .research-hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
          color: var(--black);
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .research-hero-description {
          font-size: 1.375rem;
          color: var(--gray-600);
          line-height: 1.6;
          max-width: 800px;
          margin-bottom: 3rem;
        }

        .research-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          margin-top: 4rem;
        }

        .stat-card {
          background: var(--white);
          padding: 2rem;
          border-radius: var(--radius-lg);
          text-align: center;
          box-shadow: var(--shadow-md);
          transition: all var(--transition-base);
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
        }

        .stat-number {
          font-size: 3rem;
          font-weight: 800;
          color: var(--blue-600);
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 1rem;
          color: var(--gray-600);
          font-weight: 500;
        }

        .research-tabs {
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          position: sticky;
          top: 80px;
          z-index: 100;
        }

        .tabs-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          gap: 2rem;
          overflow-x: auto;
        }

        .tab-button {
          padding: 1.5rem 0;
          background: none;
          border: none;
          font-size: 1rem;
          font-weight: 600;
          color: var(--gray-600);
          cursor: pointer;
          position: relative;
          transition: all var(--transition-fast);
          white-space: nowrap;
        }

        .tab-button:hover {
          color: var(--gray-900);
        }

        .tab-button.active {
          color: var(--blue-600);
        }

        .tab-button::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: var(--blue-600);
          transform: scaleX(0);
          transition: transform var(--transition-base);
        }

        .tab-button.active::after {
          transform: scaleX(1);
        }

        .research-content {
          padding: 4rem 2rem;
          max-width: 1280px;
          margin: 0 auto;
        }

        .search-bar {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          margin-bottom: 3rem;
        }

        .search-icon {
          width: 20px;
          height: 20px;
          color: var(--gray-500);
        }

        .search-input {
          flex: 1;
          background: none;
          border: none;
          font-size: 1rem;
          color: var(--gray-900);
          outline: none;
        }

        .search-input::placeholder {
          color: var(--gray-500);
        }

        .publications-grid {
          display: grid;
          gap: 2rem;
        }

        .publication-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
          cursor: pointer;
        }

        .publication-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .publication-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 2rem;
          margin-bottom: 1rem;
        }

        .publication-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          line-height: 1.4;
          margin-bottom: 0.5rem;
        }

        .publication-authors {
          font-size: 0.95rem;
          color: var(--gray-600);
          margin-bottom: 0.5rem;
        }

        .publication-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
          color: var(--gray-500);
        }

        .publication-journal {
          font-weight: 600;
          color: var(--blue-600);
        }

        .publication-citations {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.75rem;
          background: var(--gray-100);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
        }

        .publication-abstract {
          margin: 1.5rem 0;
          font-size: 0.95rem;
          color: var(--gray-600);
          line-height: 1.6;
        }

        .publication-tags {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .publication-tag {
          padding: 0.375rem 0.75rem;
          background: var(--blue-50);
          color: var(--blue-700);
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .patents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 2rem;
        }

        .patent-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
          position: relative;
          overflow: hidden;
        }

        .patent-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: var(--green-500);
          transform: translateX(-4px);
          transition: transform var(--transition-base);
        }

        .patent-card:hover::before {
          transform: translateX(0);
        }

        .patent-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
        }

        .patent-id {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--gray-500);
          margin-bottom: 0.5rem;
        }

        .patent-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 1rem;
          line-height: 1.4;
        }

        .patent-inventors {
          font-size: 0.95rem;
          color: var(--gray-600);
          margin-bottom: 1rem;
        }

        .patent-dates {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
          color: var(--gray-500);
          margin-bottom: 1rem;
        }

        .patent-status {
          display: inline-flex;
          padding: 0.375rem 0.75rem;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .patent-status.active {
          background: var(--green-50);
          color: var(--green-700);
        }

        .patent-status.pending {
          background: var(--yellow-50);
          color: var(--yellow-700);
        }

        .collaborations-grid {
          display: grid;
          gap: 2rem;
        }

        .collaboration-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 2rem;
          align-items: center;
          transition: all var(--transition-base);
        }

        .collaboration-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .collaboration-logo {
          width: 80px;
          height: 80px;
          background: var(--gray-100);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: 800;
          color: var(--gray-400);
        }

        .collaboration-info h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .collaboration-focus {
          font-size: 1rem;
          color: var(--gray-600);
          margin-bottom: 0.5rem;
        }

        .collaboration-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
          color: var(--gray-500);
        }

        .collaboration-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--green-50);
          color: var(--green-700);
          border-radius: var(--radius);
          font-size: 0.875rem;
          font-weight: 600;
        }

        .collaboration-status::before {
          content: '';
          width: 8px;
          height: 8px;
          background: var(--green-500);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-state-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 2rem;
          color: var(--gray-300);
        }

        .empty-state-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .empty-state-description {
          font-size: 1rem;
          color: var(--gray-600);
        }

        @media (max-width: 768px) {
          .research-hero {
            padding: 8rem 1rem 3rem;
          }

          .research-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .stat-card {
            padding: 1.5rem;
          }

          .stat-number {
            font-size: 2rem;
          }

          .tabs-container {
            padding: 0 1rem;
            gap: 1rem;
          }

          .tab-button {
            padding: 1rem 0;
            font-size: 0.875rem;
          }

          .publication-header {
            flex-direction: column;
            gap: 1rem;
          }

          .patents-grid {
            grid-template-columns: 1fr;
          }

          .collaboration-card {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .collaboration-logo {
            margin: 0 auto;
          }
        }
      `}</style>

      <section className="research-hero">
        <div className="research-hero-content">
          <div className="research-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
            </svg>
            Peer-Reviewed Research
          </div>
          <h1 className="research-hero-title">
            Pioneering the Science of Electromagnetic Computing
          </h1>
          <p className="research-hero-description">
            Our research spans theoretical foundations, practical implementations, and real-world applications of Electro-Agnostic Distributed Computing. Every publication represents a step toward computational transcendence.
          </p>

          <div className="research-stats">
            <div className="stat-card">
              <div className="stat-number">47</div>
              <div className="stat-label">Publications</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">2,341</div>
              <div className="stat-label">Citations</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">23</div>
              <div className="stat-label">Patents</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">15</div>
              <div className="stat-label">Collaborations</div>
            </div>
          </div>
        </div>
      </section>

      <div className="research-tabs">
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'publications' ? 'active' : ''}`}
            onClick={() => setActiveTab('publications')}
          >
            Publications
          </button>
          <button
            className={`tab-button ${activeTab === 'patents' ? 'active' : ''}`}
            onClick={() => setActiveTab('patents')}
          >
            Patents
          </button>
          <button
            className={`tab-button ${activeTab === 'collaborations' ? 'active' : ''}`}
            onClick={() => setActiveTab('collaborations')}
          >
            Collaborations
          </button>
          <button
            className={`tab-button ${activeTab === 'datasets' ? 'active' : ''}`}
            onClick={() => setActiveTab('datasets')}
          >
            Open Datasets
          </button>
        </div>
      </div>

      <div className="research-content">
        {activeTab === 'publications' && (
          <>
            <div className="search-bar">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder="Search publications by title, author, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="publications-grid">
              {filteredPublications.map(pub => (
                <div key={pub.id} className="publication-card">
                  <div className="publication-header">
                    <div>
                      <h3 className="publication-title">{pub.title}</h3>
                      <p className="publication-authors">{pub.authors}</p>
                      <div className="publication-meta">
                        <span className="publication-journal">{pub.journal}</span>
                        <span>•</span>
                        <span>{pub.year}</span>
                      </div>
                    </div>
                    <div className="publication-citations">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      {pub.citations}
                    </div>
                  </div>
                  <p className="publication-abstract">{pub.abstract}</p>
                  <div className="publication-tags">
                    {pub.tags.map(tag => (
                      <span key={tag} className="publication-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {filteredPublications.length === 0 && (
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <h3 className="empty-state-title">No publications found</h3>
                <p className="empty-state-description">Try adjusting your search terms</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'patents' && (
          <div className="patents-grid">
            {patents.map(patent => (
              <div key={patent.id} className="patent-card">
                <div className="patent-id">{patent.id}</div>
                <h3 className="patent-title">{patent.title}</h3>
                <p className="patent-inventors">Inventors: {patent.inventors}</p>
                <div className="patent-dates">
                  <span>Filed: {patent.filed}</span>
                  <span>Granted: {patent.granted}</span>
                </div>
                <span className={`patent-status ${patent.status}`}>
                  {patent.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'collaborations' && (
          <div className="collaborations-grid">
            {collaborations.map((collab, index) => (
              <div key={index} className="collaboration-card">
                <div className="collaboration-logo">
                  {collab.institution.split(' ')[0].substring(0, 2)}
                </div>
                <div className="collaboration-info">
                  <h3>{collab.institution}</h3>
                  <p className="collaboration-focus">{collab.focus}</p>
                  <div className="collaboration-meta">
                    <span>Lead: {collab.lead}</span>
                    <span>•</span>
                    <span>Since {collab.startDate}</span>
                  </div>
                </div>
                <div className="collaboration-status">
                  {collab.status}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'datasets' && (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            <h3 className="empty-state-title">Open Datasets Coming Soon</h3>
            <p className="empty-state-description">
              We're preparing our electromagnetic field computation datasets for public release
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ResearchPage;
